import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Building2, ShieldAlert, GitBranch, Rocket, ArrowUpRight } from "lucide-react";
import { Facility360 } from "./Facility360";

const TYPE_META = {
  facility: { icon: Building2, label: "Facility exposure", tone: "lime" },
  risk: { icon: ShieldAlert, label: "Risk", tone: "warning" },
  scenario: { icon: GitBranch, label: "Decision scenario", tone: "outline" },
  initiative: { icon: Rocket, label: "Initiative", tone: "success" },
};
const WINDOWS = { "90d": 90, "12m": 365, all: null };
const MONTH = (d) =>
  new Date(d).toLocaleDateString("en-SG", { month: "long", year: "numeric" });

export function HazardTimeline({ hazard, canEdit, onClose }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [win, setWin] = useState("all");
  const [f360, setF360] = useState(null);

  useEffect(() => {
    api.get(`/hazards/timeline/${hazard.code}`).then((r) => setData(r.data)).catch(() => setData({ entries: [] }));
    api.get("/facilities").then((r) => setFacilities(r.data.facilities)).catch(() => {});
  }, [hazard.code]);

  const entries = useMemo(() => {
    const days = WINDOWS[win];
    if (!data) return [];
    if (!days) return data.entries;
    const cutoff = Date.now() - days * 86400000;
    return data.entries.filter((e) => e.date && new Date(e.date).getTime() >= cutoff);
  }, [data, win]);

  const openFacility = (fid) => {
    const f = facilities.find((x) => x.id === fid);
    if (f) setF360(f);
  };

  let lastMonth = null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="hazard-timeline-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Badge variant="lime" className="font-mono text-[10px]">{hazard.code}</Badge> {hazard.name} — timeline
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] text-muted-foreground max-w-sm">
            Everything in your organisation tagged to this hazard, oldest first — exposures, risks, scenarios and initiatives.
          </p>
          <Select value={win} onValueChange={setWin}>
            <SelectTrigger className="w-36 h-8 text-xs" data-testid="hz-timeline-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="12m">Last 12 months</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!data && <div className="text-xs text-muted-foreground py-6">Loading timeline…</div>}
        {data && entries.length === 0 && (
          <div className="text-xs text-muted-foreground py-6" data-testid="hz-timeline-empty">
            Nothing tagged to {hazard.code} in this timeframe.
          </div>
        )}

        <div className="relative pl-5 space-y-2">
          <div className="absolute left-1.5 top-1 bottom-1 w-px bg-border" />
          {entries.map((e, i) => {
            const m = TYPE_META[e.type];
            const Icon = m.icon;
            const month = e.date ? MONTH(e.date) : "Undated";
            const header = month !== lastMonth;
            lastMonth = month;
            return (
              <React.Fragment key={`${e.type}-${e.id || e.facility_id}-${i}`}>
                {header && <div className="label-eyebrow -ml-5 pt-2">{month}</div>}
                <div className="relative border border-border rounded-sm p-2.5 text-xs bg-surface" data-testid={`hz-timeline-entry-${e.type}-${i}`}>
                  <div className="absolute -left-[15px] top-3 w-2 h-2 rounded-full bg-accent" />
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="flex items-center gap-1.5 min-w-0">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <Badge variant={m.tone} className="text-[9px]">{m.label}</Badge>
                      <span className="font-medium truncate">{e.title}</span>
                    </span>
                    <span className="font-mono text-[10px] text-muted-foreground">{e.date ? e.date.slice(0, 10) : "—"}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
                    {e.type === "facility" && (
                      <>
                        <span>{e.location_name} · level {e.level}/5</span>
                        <Badge variant={e.band === "High" || e.band === "Critical" ? "danger" : "warning"} className="text-[9px]">{e.band} · {e.score}</Badge>
                        <Button size="sm" variant="ghost" className="h-5 text-[10px] ml-auto" onClick={() => openFacility(e.facility_id)} data-testid={`hz-timeline-open-360-${i}`}>
                          Open 360° <ArrowUpRight className="h-2.5 w-2.5" />
                        </Button>
                      </>
                    )}
                    {e.type === "risk" && (
                      <>
                        {(e.hazard_codes || []).map((c) => <Badge key={c} variant="outline" className="text-[9px] font-mono">{c}</Badge>)}
                        <Badge variant="lime" className="text-[9px]">{e.lane}</Badge>
                        {e.dollar_impact_sgd ? <span className="font-mono">S${e.dollar_impact_sgd.toLocaleString()}</span> : null}
                        <Button size="sm" variant="ghost" className="h-5 text-[10px] ml-auto" onClick={() => navigate("/risk-register")} data-testid={`hz-timeline-open-risk-${i}`}>
                          Risk register <ArrowUpRight className="h-2.5 w-2.5" />
                        </Button>
                      </>
                    )}
                    {e.type === "scenario" && (
                      <>
                        <span>from: {e.risk_title}</span>
                        {e.impact_range && <span className="font-mono">S${(e.impact_range.low_sgd || 0).toLocaleString()}–{(e.impact_range.high_sgd || 0).toLocaleString()}</span>}
                        {e.facility_id && (
                          <Button size="sm" variant="ghost" className="h-5 text-[10px] ml-auto" onClick={() => openFacility(e.facility_id)} data-testid={`hz-timeline-open-scenario-${i}`}>
                            View in 360° <ArrowUpRight className="h-2.5 w-2.5" />
                          </Button>
                        )}
                      </>
                    )}
                    {e.type === "initiative" && (
                      <>
                        <Badge variant="outline" className="text-[9px]">{e.status}</Badge>
                        <Button size="sm" variant="ghost" className="h-5 text-[10px] ml-auto" onClick={() => navigate("/initiatives")} data-testid={`hz-timeline-open-initiative-${i}`}>
                          Initiatives <ArrowUpRight className="h-2.5 w-2.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {f360 && <Facility360 facility={f360} canEdit={canEdit} onClose={() => setF360(null)} onSaved={() => api.get("/facilities").then((r) => setFacilities(r.data.facilities)).catch(() => {})} />}
      </DialogContent>
    </Dialog>
  );
}
